import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AppConfigService } from 'app/core/config/app-config.service';
import { StepAudioCaptureService } from 'app/services/step-audio-capture.service';
import { MediaDeviceCoordinatorService } from 'app/services/media-device-coordinator.service';

export type VoiceStatus = 'idle' | 'listening' | 'paused' | 'error';

export interface VoiceState {
  status: VoiceStatus;
  /** Accumulated final transcript text (appended as recognition fires) */
  finalText: string;
  /** Current unconfirmed (interim) transcript fragment */
  interimText: string;
  /** Error message if status === 'error' */
  errorMessage: string | null;
}

@Injectable({ providedIn: 'root' })
export class VoiceInputService implements OnDestroy {
  private _state$ = new BehaviorSubject<VoiceState>({
    status: 'idle',
    finalText: '',
    interimText: '',
    errorMessage: null,
  });
  readonly state$ = this._state$.asObservable();

  private recognition: any = null;
  private stream: MediaStream | null = null;
  private generation = 0;
  private lease: string | null = null;
  private invalidation: Subscription;

  /** When true the user has explicitly toggled recording ON; drives auto-restart. */
  private _userWantsActive = false;
  /** Recognition language (BCP-47). Settable before start(). */
  private _locale = 'en-US';
  /** Prevent restart loops when deliberately stopping. */
  private _stopping = false;

  constructor(private http: HttpClient, private appConfig: AppConfigService, private capture: StepAudioCaptureService, private mediaHub: MediaDeviceCoordinatorService) {
    this.invalidation = mediaHub.getLeasesInvalidated().subscribe(ids => {
      if (this._userWantsActive && this.lease && ids.includes(this.lease)) void this.acquire(this.generation).catch(e => this._patch({ errorMessage: String(e) }));
    });
  }

  private async acquire(generation: number): Promise<void> {
    const lease = 'answer-dictation-' + generation;
    const stream = await this.mediaHub.checkoutMicStream(lease);
    if (generation !== this.generation || !this._userWantsActive) { this.mediaHub.release(lease); return; }
    this.lease = lease;
    this.stream = stream;
    this.capture.attach('input', stream);
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  get snapshot(): VoiceState { return this._state$.getValue(); }

  setLocale(locale: string): void { this._locale = locale; }

  /**
   * Request mic + start both SpeechRecognition and MediaRecorder.
   * Resolves once the stream is open; rejects on permission denial.
   */
  async start(initialText = '', studentStepId?: number): Promise<void> {
    if (this._userWantsActive) return; // already running
    this._stopping = false;
    this._userWantsActive = true;
    const generation = ++this.generation;
    this._patch({ finalText: initialText, interimText: '', errorMessage: null });

    try {
      if (studentStepId) this.capture.bindStep(studentStepId);
      await this.acquire(generation);
      if (generation !== this.generation || !this._userWantsActive) return;
    } catch (err: any) {
      this._userWantsActive = false;
      this._patch({ status: 'error', errorMessage: err?.message || 'Microphone access denied' });
      throw err;
    }

    this._startRecognition();
    this._startMediaRecorder();
    this._patch({ status: 'listening' });
  }

  /**
   * Toggle: if listening/paused -> stop+upload; if idle/error -> start.
   * Returns a promise so callers can await the upload before submitting.
   */
  async toggle(studentStepId: number, currentText = ''): Promise<void> {
    if (this._userWantsActive) {
      await this.stop(studentStepId);
    } else {
      await this.start(currentText, studentStepId);
    }
  }

  /**
   * Resume after auto-stop (recognition ended but user hasn't toggled off).
   * Only restarts the recognition engine, not the media recorder.
   */
  resumeRecognition(): void {
    if (!this._userWantsActive || this.snapshot.status !== 'paused') return;
    this._stopping = false;
    this._startRecognition();
    this._patch({ status: 'listening' });
  }

  /**
   * Stop recognition and upload the recorded audio to the server.
   * Resolves after upload completes (or on upload error — non-fatal).
   */
  stop(studentStepId: number): Promise<void> {
    this.generation++;
    this._userWantsActive = false;
    this._stopping = true;

    if (this.recognition) {
      try { this.recognition.stop(); } catch {}
      this.recognition = null;
    }

    const preserved = this.capture.detach('input');
    this._cleanupStream();
    this._patch({ status: 'idle', interimText: '' });
    return preserved;
  }

  /**
   * Send one recording to the step's voice history. Recordings accumulate on
   * the server, so this never replaces an earlier one.
   *
   * Audio is durably queued before returning; failed delivery retries later.
   *
   * @param source `input` for a typed-answer voice recording, `ai` for a
   *   capture made while the AI tutor was running the step.
   * @param speechWindows Offsets in milliseconds of the stretches where the
   *   student was actually speaking, so the player can skip the silence.
   */
  uploadRecording(
    studentStepId: number,
    blob: Blob,
    opts: {
      source: 'ai' | 'input';
      durationMs?: number;
      speechWindows?: { startMs: number; endMs?: number }[];
    }
  ): Promise<void> {
    return this.capture.preserveBlob(studentStepId, blob, opts);
  }

  /** Stop input and preserve audio, including when switching to typing.
   * @param preserveText When true the finalText is kept intact so the caller
   *   can transfer it to a keyboard editor without data loss. Pass false (default)
   *   when navigating to a new step where the text must be cleared.
   */
  cancel(preserveText = false): void {
    this.generation++;
    this._userWantsActive = false;
    this._stopping = true;
    if (this.recognition) { try { this.recognition.stop(); } catch {} this.recognition = null; }
    this.capture.attach('input', null);
    this._cleanupStream();
    this._patch(preserveText
      ? { status: 'idle', interimText: '' }
      : { status: 'idle', interimText: '', finalText: '' });
  }

  /**
   * Sync the user-edited text back into the service so subsequent speech
   * results append to the correct baseline, not the pre-edit version.
   */
  syncText(text: string): void {
    this._patch({ finalText: text });
  }

  ngOnDestroy(): void { this.cancel(); this.invalidation.unsubscribe(); }

  // ─── Private ───────────────────────────────────────────────────────────────

  private _startRecognition(): void {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    this.recognition = new SpeechRecognition();
    this.recognition.lang = this._locale;
    this.recognition.continuous = true;
    this.recognition.interimResults = true;

    this.recognition.onresult = (event: any) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          this._patch({ finalText: this.snapshot.finalText + transcript + ' ' });
        } else {
          interim += transcript;
        }
      }
      this._patch({ interimText: interim });
    };

    this.recognition.onend = () => {
      if (this._stopping) return;
      // Auto-stopped (browser timeout / silence detection) — surface paused state
      // so the user can see and manually resume.
      if (this._userWantsActive) {
        this._patch({ status: 'paused', interimText: '' });
      }
    };

    this.recognition.onerror = (event: any) => {
      if (this._stopping) return;
      const ignorable = ['no-speech', 'audio-capture', 'aborted'];
      if (ignorable.includes(event.error) && this._userWantsActive) {
        // Surface as paused so user can resume
        this._patch({ status: 'paused', interimText: '' });
      } else if (!ignorable.includes(event.error)) {
        // Recognition is optional; microphone retention continues until explicit stop.
        this._patch({ status: 'paused', errorMessage: event.error || 'Recognition unavailable; audio is still recording' });
      }
    };

    try { this.recognition.start(); } catch {}
  }

  private _startMediaRecorder(): void {
    if (this.stream) this.capture.attach('input', this.stream);
  }

  private _cleanupStream(): void {
    if (this.stream) {
      this.capture.attach('input', null);
      if (this.lease) this.mediaHub.release(this.lease);
      this.lease = null;
      this.stream = null;
    }
  }

  private _patch(partial: Partial<VoiceState>): void {
    this._state$.next({ ...this._state$.getValue(), ...partial });
  }
}

